/**
 * Planned's "Suggested recurring bills" (TMMAPP-0141 §B, the setup wizard's step 5 in its home): a
 * quiet card at the top of the page, only while the history has bill proposals. Add, Adjust, Not a
 * bill and Add all act as they did in the wizard; nothing is created until the person does.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { errorCopy } from '@/api/errorCopy';
import { genesisApi } from '@/api/genesis';
import { useNotify } from '@/shell/NotifyProvider';
import { focusAfterRemoval } from './focusAfterRemoval';
import { SeriesProposalsStep } from './SeriesProposalsStep';
import type { GenesisProposals } from './useGenesisProposals';

interface SuggestedRecurringBillsProps {
  /** The accounts Planned edits: the Adjust dialog's choices and each card's account name. */
  accounts: Account[];
  /** A bill was added: Planned reads its series and calendar again. */
  onAdded: () => void;
  /** The proposals, read by the page: their count is its Items to review count (TMMAPP-0159 §F 28). */
  source: GenesisProposals;
}

export function SuggestedRecurringBills({
  accounts,
  onAdded,
  source,
}: SuggestedRecurringBillsProps) {
  const { t } = useTranslation();
  const notify = useNotify();
  const headingId = useId();
  const { proposals, reload } = source;
  const [busy, setBusy] = useState(false);
  // The acted-on row's index: once the write and the re-read settle (the buttons are enabled
  // again), focus moves to the row now in its place, or to the page's h1 (TMMAPP-0143 item 2).
  const sectionRef = useRef<HTMLElement | null>(null);
  const focusIndex = useRef<number | null>(null);
  useEffect(() => {
    if (busy || focusIndex.current === null) return;
    const index = focusIndex.current;
    focusIndex.current = null;
    focusAfterRemoval(sectionRef.current, index);
  }, [busy, proposals]);

  if (proposals.length === 0) return null;

  /** One write, then the section and (when something was created) the page read again. */
  const act = (write: () => Promise<string | null>, created: boolean, index: number) => {
    setBusy(true);
    void write()
      .then(async (message) => {
        if (message !== null) notify(message);
        if (created) onAdded();
        focusIndex.current = index;
        await reload();
      })
      .catch((error: unknown) => {
        notify(errorCopy(t, error, 'accounts.errorGeneric'), { severity: 'error' });
      })
      .finally(() => setBusy(false));
  };

  const indexOf = (id: number) => proposals.findIndex((p) => p.id === id);

  return (
    <Card
      ref={sectionRef}
      variant="outlined"
      component="section"
      aria-labelledby={headingId}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography id={headingId} variant="h2">
              {t('genesis.suggestedBills', { count: proposals.length })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('genesis.suggestedBillsBody')}
            </Typography>
          </Stack>
          <SeriesProposalsStep
            proposals={proposals}
            accounts={accounts}
            busy={busy}
            onAccept={(id, overrides) =>
              act(
                async () => {
                  await genesisApi.accept(id, overrides ?? {});
                  return t('genesis.billAdded');
                },
                true,
                indexOf(id),
              )
            }
            onDismiss={(id) =>
              act(
                async () => {
                  await genesisApi.dismiss(id);
                  return null;
                },
                false,
                indexOf(id),
              )
            }
            onAcceptAll={() =>
              act(
                async () => {
                  const result = await genesisApi.acceptAll('SERIES');
                  return t('genesis.acceptedAllSeries', {
                    count: result.accepted,
                    asked: proposals.length,
                  });
                },
                true,
                0,
              )
            }
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
